import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/textarea/textarea.js';

import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';

import { callLambda } from '../../../../helpers/lambda.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';
import {
  parseVatNameSnapshot,
  vatNameDecisionResult,
  type VatNameDecision,
  type VatNameDecisionAnswer,
  type VatNameSnapshot,
} from '../company/hmrc/trust-score-logic.js';
import definition from '@job-schemas/support/hmrc-vat-name-review.json';
import { jobSchema } from '../../job-schema.js';

/**
 * The nodeStream support card hmrc/auth/complete raises when a validation card's VAT register name is not the company's
 * Companies House name (live HMRC only). Support claims it, compares the names, and approves or rejects the VAT number
 * through hmrc/vat-name-review, which completes this job itself: the card never calls job/complete.
 */
@customElement(NSSupportHMRCVatNameReview.is)
export default class NSSupportHMRCVatNameReview extends NSJobComponent {
  static get is() {
    return 'ns-support-hmrc-vat-name-review';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { approved: { type: 'hidden' }, rejected: { type: 'hidden' } } });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  @state()
  private __busy = false;

  @state()
  private __notes = '';

  @state()
  private __snapshot: VatNameSnapshot | null = null;

  // The decision's answer, once hmrc/vat-name-review has decided or closed the review
  @state()
  private __result: string | null = null;

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;
    if (!this._job) return;
    this.__readSnapshot();
    // hmrc/auth/complete puts a changed pending review on this card's job: show it as soon as it lands
    this._subscriptions.subscribe(`job.${this._job.id}.*`, () => this.__readSnapshot());
  }

  private __readSnapshot() {
    this.__snapshot = parseVatNameSnapshot(this._job?.metadata.find((m) => m.key === 'vatNameReview')?.value);
  }

  private get __companyName(): string {
    const name = this._job?.inputValues?.subjectCompanyName;
    return typeof name === 'string' && name ? name : 'This company';
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  // A STALE_REVIEW refusal means the review changed under the card: read the job again so the latest one shows
  private async __reloadJob() {
    if (!this._db || !this._job) return;
    try {
      const fresh = (await this._db.getById('job', this._job.id)) as {
        metadata?: { key: string; value: string | null }[];
      };
      if (fresh?.metadata) this._job.metadata = fresh.metadata;
    } catch {
      // The realtime update still brings it
    }
    this.__readSnapshot();
  }

  private async __decide(decision: VatNameDecision) {
    const snapshot = this.__snapshot;
    if (!this._job || !snapshot || this.__busy) return;
    this.__busy = true;
    this.clearError();
    this.loading(decision === 'APPROVE' ? 'Approving the VAT number' : 'Rejecting the VAT number');
    try {
      const notes = this.__notes.trim();
      const body = {
        jobId: this._job.id,
        decision,
        requestedAt: snapshot.requestedAt,
        ...(notes ? { notes } : {}),
      };
      const response = await callLambda('hmrc/vat-name-review', 'POST', this._auth?.token, JSON.stringify(body));
      const result = vatNameDecisionResult(response.res as unknown as VatNameDecisionAnswer);
      if (result.kind === 'refused') {
        this.showError(result.message);
        if (result.reload) await this.__reloadJob();
        return;
      }
      // The lambda completed this job
      this.__result = result.message;
      this._jobComplete();
    } catch (err) {
      this.showError(err, 'The decision could not be saved. Please try again.');
    } finally {
      this.loaded();
      this.__busy = false;
    }
  }

  private __renderReview() {
    const snapshot = this.__snapshot;
    if (!snapshot) {
      return html`
        <div class="job-panel">
          <p role="alert">This card has no VAT registration name review to show. Contact the nodeStream team.</p>
        </div>
      `;
    }
    return html`
      <div class="job-panel">
        <h3>The names to compare</h3>
        <ns-detail-list
          .rows=${[
            { label: 'Companies House name', value: snapshot.companyName },
            { label: 'VAT register name', value: snapshot.vatName || 'HMRC gave no name' },
            { label: 'VAT number', value: snapshot.vrn },
          ]}
        ></ns-detail-list>
        <p>
          Approve if this VAT number belongs to the company, for example a VAT group or a trading name. Reject if it
          belongs to another business: the company is then asked to connect HMRC again with the right one.
        </p>
        ${!this._job || isUnclaimed(this._job)
          ? nothing
          : html`
              <wa-textarea
                label="Notes"
                hint="They stay with this card; the company doesn't see them."
                .value=${this.__notes}
                ?disabled=${this.__busy}
                @input=${(e: Event) => {
                  this.__notes = (e.target as HTMLTextAreaElement).value;
                }}
              ></wa-textarea>
            `}
      </div>
    `;
  }

  render() {
    return html`
      <ns-job-layout>
        <p slot="intro">
          ${this.__companyName}'s VAT registration name is not its Companies House name, so check its VAT number before
          it can sign its HMRC validation.
        </p>
        ${this.__result
          ? html`
              <div class="job-panel">
                <p role="status">${this.__result}</p>
              </div>
            `
          : this.__renderReview()}
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>About this review</h3>
          <p>
            HMRC confirmed this VAT number for the person who connected it, but the name on HMRC's VAT register is not
            the company's Companies House name. Until you decide, the company cannot sign its HMRC validation.
          </p>
          <p class="note">Claim the card before you decide; only the person who claimed it can.</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE' || this.__result) return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    const disabled = this.__busy || !this.__snapshot;
    return html`
      <wa-button variant="brand" type="button" ?disabled=${disabled} @click=${() => this.__decide('APPROVE')}>
        Approve
      </wa-button>
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${disabled}
        @click=${() => this.__decide('REJECT')}
      >
        Reject
      </wa-button>
    `;
  }
}
